import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'
import { extractErrorMessage } from '../../utils/apitools'

/**
 * Admin → Demos (design/2026-10-22-chief-of-staff-and-demos.md T19, T20):
 * the demo stories goapi embeds, the demo-customer mark, Reset (a dry run,
 * then a background run polled here) and a story's staged steps. Every
 * route is Manager only on the server, except the job-info read that the
 * Demo badge and the step button use. Routes: goapi/pkg/server/admin_demos.go.
 */

export interface DemoFunnelStage {
  label: string
  value: number
}

export interface DemoStepStatus {
  id: string
  job: string
  label: string
  done: boolean
  funnel: DemoFunnelStage[]
}

export interface DemoLastReset {
  env: string
  at: string
  /** Who clicked: on the Managers' Demos page only, never in job info. */
  by?: string
}

export interface DemoStory {
  story: string
  title: string
  customer: string
  is_demo_customer: boolean
  last_reset: DemoLastReset | null
  /**
   * The server's own environment (SENTRY_ENVIRONMENT), the one a Reset here
   * writes into the marker. Compared with last_reset.env; never a Vite build
   * variable, because one frontend image serves staging and production.
   */
  env?: string
  /** A Reset or step running now ("reset", "step scan-social"). */
  running?: string
  steps: DemoStepStatus[]
  warnings?: string[]
  /** The story's Markdown guide for the presenter: what to do, in order. */
  guide?: string
}

/** What a Reset would delete (POST …/reset?dry_run=1). */
export interface DemoPlan {
  story: string
  customer: string
  counts: Record<string, number>
  blobs: Record<string, number>
  warnings: string[]
}

export interface DemoPhase {
  name: string
  job?: string
  detail?: string
}

export interface DemoRun {
  run_id: string
  story: string
  kind: string
  by?: string
  status: 'running' | 'succeeded' | 'failed'
  phase?: DemoPhase
  done: boolean
  error?: string
  warnings?: string[]
  started_at?: string
  finished_at?: string
}

/** GET /admin/demos/job/:customer/:job: the badge and the step button. */
export interface DemoJobInfo {
  is_demo: boolean
  story?: string
  title?: string
  running?: string
  last_reset?: DemoLastReset | null
  steps: DemoStepStatus[]
}

/** How often a running Reset or step is polled. */
export const DEMO_RUN_POLL_MS = 1500

// ── pure rules ────────────────────────────────────────────────────────────

/**
 * Whether a confirmation box holds the customer's name, exactly (spaces
 * around it aside). The server checks the same name in the request body:
 * a word like "reset" is the same for every customer and typed from habit.
 */
export function customerTyped(text: string, customer: string): boolean {
  return customer !== '' && text.trim() === customer
}

/**
 * The environment the last Reset ran in when it is not this one, else null.
 * `here` is the server's environment (DemoStory.env); when the server does not
 * say, nothing is claimed.
 */
export function lastResetElsewhere(last: DemoLastReset | null | undefined, here: string | undefined): string | null {
  if (!last?.env || !here) return null
  return last.env === here ? null : last.env
}

const PHASE_WORDS: Record<string, string> = {
  guard: 'Checking the demo customer',
  preflight: 'Reading the story’s files',
  'wipe-chats': 'Deleting agent chats',
  'wipe-data': 'Deleting the customer’s data',
  'ensure-jobs': 'Making the story’s jobs',
  'wipe-files': 'Deleting the jobs’ files',
  seed: 'Loading the story’s data',
  caches: 'Clearing caches',
  marker: 'Recording the Reset',
  step: 'Playing the step',
  done: 'Finished',
}

/** A run's phase in words. */
export function phaseLabel(phase: DemoPhase | undefined): string {
  if (!phase?.name) return 'Starting'
  const words = PHASE_WORDS[phase.name] ?? phase.name
  const what = phase.job || phase.detail
  return what && phase.name !== 'done' ? `${words}: ${what}` : words
}

/** The dry run as rows: tables with rows to delete (largest first), and files per job. */
export function planRows(plan: DemoPlan): {
  tables: { name: string; count: number }[]
  files: { job: string; count: number }[]
  total: number
} {
  const tables = Object.entries(plan.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  const files = Object.entries(plan.blobs ?? {})
    .map(([job, count]) => ({ job, count }))
    .sort((a, b) => a.job.localeCompare(b.job))
  const total = tables.reduce((n, t) => n + t.count, 0) + files.reduce((n, f) => n + f.count, 0)
  return { tables, files, total }
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

/**
 * The funnel's figures at progress p (0..1): the stages count up one after
 * another, each in an equal share of the time, eased, never past its value.
 */
export function funnelValuesAt(funnel: DemoFunnelStage[], p: number): number[] {
  const n = funnel.length
  if (n === 0) return []
  const progress = Math.min(1, Math.max(0, p))
  return funnel.map((stage, i) => {
    const local = Math.min(1, Math.max(0, progress * n - i))
    if (local >= 1) return stage.value
    return Math.min(stage.value, Math.floor(stage.value * easeOut(local)))
  })
}

/** A job's steps that have not run since the last Reset. */
export function pendingStepsForJob(steps: DemoStepStatus[], job: string): DemoStepStatus[] {
  return steps.filter((s) => s.job === job && !s.done)
}

// ── server calls ──────────────────────────────────────────────────────────

const storiesKey = ['admin', 'demos'] as const
const jobInfoKey = (customer: string, job: string) => ['admin', 'demos', 'job', customer, job] as const
const runKey = (runId: string) => ['admin', 'demos', 'runs', runId] as const

const asError = (error: unknown): Error => new Error(extractErrorMessage(error) || 'The request failed')
const enc = encodeURIComponent

/** GET /admin/demos: every story with its customer's mark, last Reset and steps. */
export function useDemoStories(enabled = true) {
  const query = useQuery<DemoStory[], Error>({
    queryKey: storiesKey,
    enabled,
    queryFn: async () => {
      try {
        return (await axios.get<DemoStory[]>(`${API_BASE_URL}/admin/demos`)).data ?? []
      } catch (error) {
        throw asError(error)
      }
    },
  })
  return {
    stories: query.data ?? [],
    storiesLoading: query.isLoading,
    storiesError: query.error?.message ?? null,
    refetchStories: query.refetch,
  }
}

/**
 * PUT / DELETE /admin/demos/customers/:customer: mark or unmark a demo
 * customer. confirm is the customer's name as typed; the server refuses
 * (400) unless it is the customer.
 */
export function useMarkDemoCustomer() {
  const queryClient = useQueryClient()
  const mutation = useMutation<unknown, Error, { customer: string; mark: boolean; confirm: string }>({
    mutationFn: async ({ customer, mark, confirm }) => {
      try {
        const url = `${API_BASE_URL}/admin/demos/customers/${enc(customer)}`
        return (mark ? await axios.put(url, { confirm }) : await axios.delete(url, { data: { confirm } })).data
      } catch (error) {
        throw asError(error)
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: storiesKey }),
  })
  return { setMark: mutation.mutateAsync, isMarking: mutation.isPending }
}

/** POST /admin/demos/:story/reset?dry_run=1: what a Reset would delete, writing nothing. */
export async function fetchResetPlan(story: string): Promise<DemoPlan> {
  try {
    return (await axios.post<DemoPlan>(`${API_BASE_URL}/admin/demos/${enc(story)}/reset?dry_run=1`)).data
  } catch (error) {
    throw asError(error)
  }
}

/**
 * Starts a Reset or a step (202 {run_id}) and polls the run until it ends.
 * onFinished runs once when the polled run is first seen finished.
 */
export function useDemoRun(onFinished?: (run: DemoRun) => void) {
  const queryClient = useQueryClient()
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished
  const finishedIds = useRef(new Set<string>())
  const [runId, setRunId] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)

  const runQuery = useQuery<DemoRun, Error>({
    queryKey: runKey(runId ?? ''),
    enabled: Boolean(runId),
    queryFn: async () => {
      let run: DemoRun
      try {
        run = (await axios.get<DemoRun>(`${API_BASE_URL}/admin/demos/runs/${enc(runId ?? '')}`)).data
      } catch (error) {
        throw asError(error)
      }
      // Tell the caller once per run that it has ended (outside a render).
      if (run?.done && !finishedIds.current.has(run.run_id)) {
        finishedIds.current.add(run.run_id)
        void queryClient.invalidateQueries({ queryKey: ['admin', 'demos'] })
        onFinishedRef.current?.(run)
      }
      return run
    },
    refetchInterval: (query) => (query.state.status !== 'error' && query.state.data && !query.state.data.done ? DEMO_RUN_POLL_MS : false),
  })

  const run = runId ? runQuery.data : undefined

  const startWith = async (url: string, body?: object): Promise<string | null> => {
    setStartError(null)
    try {
      const { data } = body ? await axios.post<{ run_id: string }>(url, body) : await axios.post<{ run_id: string }>(url)
      setRunId(data.run_id)
      void queryClient.invalidateQueries({ queryKey: storiesKey })
      return data.run_id
    } catch (error) {
      setStartError(asError(error).message)
      return null
    }
  }

  return {
    run,
    runError: runId ? runQuery.error?.message ?? null : null,
    startError,
    /** confirm is the customer's name as typed; the server refuses (400) unless it matches. */
    startReset: (story: string, confirm: string) => startWith(`${API_BASE_URL}/admin/demos/${enc(story)}/reset`, { confirm }),
    startStep: (story: string, step: string) => startWith(`${API_BASE_URL}/admin/demos/${enc(story)}/steps/${enc(step)}`),
    clear: () => {
      setRunId(null)
      setStartError(null)
    },
  }
}

/**
 * GET /admin/demos/job/:customer/:job: whether a job is a demo job, with its
 * steps. Any member of the customer may read it; a refusal or a failure
 * reads as "not a demo job", so the badge never gets in the way.
 */
export function useDemoJobInfo(customer: string | undefined, job: string | undefined) {
  const enabled = Boolean(customer && job)
  const query = useQuery<DemoJobInfo>({
    queryKey: jobInfoKey(customer ?? '', job ?? ''),
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const data = (await axios.get<DemoJobInfo>(`${API_BASE_URL}/admin/demos/job/${enc(customer ?? '')}/${enc(job ?? '')}`)).data
        return data && typeof data === 'object' ? { ...data, steps: data.steps ?? [] } : { is_demo: false, steps: [] }
      } catch {
        return { is_demo: false, steps: [] }
      }
    },
  })
  return { info: enabled ? query.data : undefined, refetchInfo: query.refetch }
}
